/**
 * Firm configuration — what the platform knows, and the rules you add to it.
 *
 * Three tabs: the checks that run over every file (the platform's analyses plus
 * the firm's classification rules), the column names already recognised, and
 * the form for writing a new rule.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	Box,
	Switch,
	Tab as MuiTab,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';
import { Card, ErrorNotice, Note, Spinner } from '../../shared/ui';
import { useModule } from '../modules/useModule';
import type { ModuleCode } from '../modules/modules';
import { ModuleSelector } from '../modules/ModuleSelector';
import RuleList from './RuleList';
import { PageHeader } from '../../provider/pageTitle';
import { count } from '../../utils/format';
import {
	useAnalysisDefinitionsQuery,
	useCanonicalFieldsQuery,
	useFirmAliasesQuery,
	useFirmAnalysisSettingsQuery,
	useFirmRulesQuery,
	useUpdateAnalysisSettingMutation,
} from '../../queries/firm';
import { useAuth } from '../../provider/auth';
import { useToast } from '../../provider/toast';
import {
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
} from '../shared/tables';
import { EmptyState, ErrorState } from '../shared/states';
import { dataTypeLabel } from '../analyses/analysisRows';
import RuleBuilder from './RuleBuilder';
import type {
	AnalysisDefinition,
	CanonicalField,
	ClientAnalysis,
	FirmAlias,
	FirmAnalysisSetting,
	WireClassificationRule as FirmClassificationRule,
} from '../../schemas';

const TABS = ['checks', 'aliases', 'new', 'settings'] as const;
type Tab = (typeof TABS)[number];

/* ── what the endpoints this screen is not already typed for answer ────────
   `GET /firm/classification-rules` and `GET /firm/aliases` return the wire
   shape `firmService.presentRule` / `presentAlias` build — snake_case, and
   not the same field names as `types/index.ts`'s `ClassificationRule` (which
   mirrors the raw Prisma row instead, camelCase). This is the shape the
   endpoint actually answers with. */

export default function FirmConfiguration() {
	const { tab } = useParams();
	const navigate = useNavigate();
	const { permissions } = useAuth();
	/* This screen is reachable by anyone with `mapFormats` (App.tsx's route
     guard) — data entry included, so the person who knows what a code like
     ZTNR means can see the rules that will classify it. But two things on
     this screen are still `manageUsers`-only in `firmRouter`, unchanged:
     creating a rule (`POST /firm/classification-rules`), and reading
     aliases at all (`GET /firm/aliases` has no `mapFormats` exception —
     column-name spellings are a different resource from classification
     rules, not part of what a file-level decision needs). `isAdmin` gates
     both: the "Rule configuration" tab and the "Column names" tab, so a
     visitor without `manageUsers` sees only tabs whose data actually loads,
     never a control or a tab that would 403. */
	const isAdmin = permissions?.manageUsers ?? false;
	/* Column names and classification rules are per module; the selector on
     this page picks which module's are shown and written. */
	const { module, link } = useModule();
	/* H2R has no classification, so it has no "Classification rules" tab. */
	const showRules = module !== 'H2R';
	const requested = TABS.includes(tab as Tab) ? (tab as Tab) : 'checks';
	const active: Tab =
		(requested === 'checks' || isAdmin) &&
		(requested !== 'new' || showRules)
			? requested
			: 'checks';
	const fields = useCanonicalFieldsQuery(module);
	const firmAliases = useFirmAliasesQuery(isAdmin, module);
	const analyses = useAnalysisDefinitionsQuery(module);
	/* `?status=ACTIVE` is load-bearing, not a default being spelled out —
     `listClassificationRules` answers DRAFT otherwise, which is whatever an
     admin happens to be mid-edit on rather than what the engine runs today.
     This tab's whole subject is what runs over the data, and it is read by
     two audiences with different powers: an admin, and the data-entry user
     Task 9 opened it to so they could see what will fire on a file they are
     handling. Both get the same, live answer.

     Reading DRAFT for admins was the earlier fix and it was wrong in the
     ordinary case: a firm with four published rules and no draft in progress
     showed an admin an EMPTY table — Finding 3's own scenario, surviving for
     the audience that could act on it. The draft is surfaced below as a
     pointer, not as the table's contents. */
	const ownRules = useFirmRulesQuery('ACTIVE', true, module);
	/* Admins only, and only to say whether unpublished work is waiting — never
     to fill the table above with it. */
	const draftRules = useFirmRulesQuery('DRAFT', isAdmin, module);

	if (analyses.isPending) return <Spinner label="Loading…" />;
	if (analyses.error)
		return (
			<ErrorNotice error={analyses.error} onRetry={analyses.refetch} />
		);

	return (
		<div className="stack">
			<PageHeader title="Configuration" />

			<ModuleSelector />

			<Tabs
				value={active}
				aria-label="Configuration sections"
				/* On a white bar, like the audit tab bar inside its card, so the tabs
           read as a control and not as text on the page. */
				sx={{
					bgcolor: 'var(--surface)',
					border: '1px solid var(--line)',
					borderRadius: 'var(--r-lg)',
					boxShadow: 'var(--e1)',
					px: 'var(--s3)',
				}}
			>
				<MuiTab
					value="checks"
					label="Analyses"
					component={Link}
					to={link('/firm/configuration')}
				/>
				{isAdmin && (
					<MuiTab
						value="aliases"
						label="Column names"
						component={Link}
						to={link('/firm/configuration/aliases')}
					/>
				)}
				{isAdmin && showRules && (
					<MuiTab
						value="new"
						label="Classification rules"
						component={Link}
						to={link('/firm/configuration/new')}
					/>
				)}
				{isAdmin && (
					<MuiTab
						value="settings"
						label="Analysis settings"
						component={Link}
						to={link('/firm/configuration/settings')}
					/>
				)}
			</Tabs>

			{active === 'settings' && isAdmin && (
				<AnalysisSettingsTab module={module} />
			)}
			{active === 'checks' && (
				<ChecksTab
					rows={analyses.data ?? []}
					own={ownRules.data?.rules ?? []}
					ownError={ownRules.error}
					onRetryOwn={ownRules.refetch}
					draftCount={
						isAdmin ? (draftRules.data?.rules.length ?? 0) : 0
					}
				/>
			)}
			{active === 'aliases' && isAdmin && (
				<NamesTab
					baseline={fields.data?.fields ?? []}
					firm={firmAliases.data?.aliases ?? []}
				/>
			)}
			{active === 'new' && isAdmin && showRules && (
				<div className="stack">
					{/* The list ABOVE the builder: seeing what already exists is what
              stops a fifth rule being written that the pass will never reach,
              and it is where editing, deleting and publishing live. */}
					<RuleList
						onChanged={() => {
							void ownRules.refetch();
							void draftRules.refetch();
						}}
					/>
					<RuleBuilder
						onSaved={() => {
							void ownRules.refetch();
							void draftRules.refetch();
							navigate(link('/firm/configuration'));
						}}
					/>
				</div>
			)}
		</div>
	);
}

/* ═══ column names ═════════════════════════════════════════════════════════ */

interface NameRow {
	heading: string;
	canonicalField: string;
	scope: 'Built in' | 'Added by your firm';
}

function NamesTab({
	baseline,
	firm,
}: {
	baseline: CanonicalField[];
	firm: FirmAlias[];
}) {
	const [filters, setFilters] = useState({ search: '' });
	const fullScreen = useFullScreen('Column names');

	const rows: NameRow[] = [
		...baseline.flatMap((field) =>
			field.aliases.map((heading) => ({
				heading,
				canonicalField: field.label,
				scope: 'Built in' as const,
			})),
		),
		...firm.map((alias) => ({
			heading: alias.spelling,
			canonicalField:
				baseline.find((field) => field.field === alias.canonicalField)
					?.label ?? alias.canonicalField,
			scope: 'Added by your firm' as const,
		})),
	];

	const term = filters.search.trim().toLowerCase();
	const visible = term
		? rows.filter((row) =>
				`${row.heading} ${row.canonicalField}`
					.toLowerCase()
					.includes(term),
			)
		: rows;

	const columns: Array<Column<NameRow>> = [
		{
			key: 'heading',
			label: 'Heading in the file',
			width: 300,
			sortValue: (row) => row.heading,
			render: (row) => row.heading,
		},
		{
			key: 'canonical',
			label: 'Standard field',
			width: 260,
			sortValue: (row) => row.canonicalField,
			render: (row) => row.canonicalField,
		},
		{
			key: 'scope',
			label: 'Source',
			width: 140,
			sortValue: (row) => row.scope,
			render: (row) => row.scope,
		},
	];

	return (
		<>
			<FilterBar
				specs={[
					{
						key: 'search',
						label: 'Search',
						kind: 'search',
						placeholder: 'Heading or standard field',
					},
				]}
				values={filters}
				onChange={(_key, value) => setFilters({ search: value })}
				onClear={() => setFilters({ search: '' })}
				resultNote={`${count(visible.length)} of ${count(rows.length)}`}
				actions={fullScreen.button}
			/>
			<Card>
				<DataTable
					rows={visible}
					columns={columns}
					rowKey={(row) =>
						`${row.heading}-${row.canonicalField}-${row.scope}`
					}
					caption="Column names"
					fullScreen={fullScreen}
					maxHeight="main"
					empty={
						<EmptyState title="Nothing matches that">
							Clear the search to see every column name.
						</EmptyState>
					}
				/>
			</Card>
		</>
	);
}

/* ═══ checks ═══════════════════════════════════════════════════════════════ */

interface CheckRow {
	id: string;
	mine: boolean;
	code: string;
	name: string;
	does: string;
}

function ChecksTab({
	rows,
	own,
	ownError,
	onRetryOwn,
	draftCount,
}: {
	rows: AnalysisDefinition[];
	/** Always the ACTIVE ruleset: what is classifying rows today, for every
	 *  audience of this tab. */
	own: FirmClassificationRule[];
	/** Surfaced rather than swallowed — without it, a failed read is
	 *  indistinguishable on screen from a firm that has written no rules, which
	 *  is the same false reading Finding 3 was about. */
	ownError: unknown;
	onRetryOwn: () => void;
	/** Unpublished rules waiting in a draft, for an admin who can publish them.
	 *  Zero for everyone else, who has no such surface. */
	draftCount: number;
}) {
	const [filters, setFilters] = useState({ search: '' });
	const fullScreen = useFullScreen('Analyses and classification rules');
	const fieldsQuery = useCanonicalFieldsQuery();
	const { vocabulary } = useAuth();
	const fieldLabel = (key: string) =>
		fieldsQuery.data?.fields.find((field) => field.field === key)?.label ??
		key;
	const classLabel = (key: string) =>
		vocabulary?.classification?.[key]?.label ?? key;

	const flat: CheckRow[] = [
		...own.map((rule, index) => ({
			id: rule.pkClassificationRuleId,
			mine: true,
			code: `R-${String(index + 1).padStart(2, '0')}`,
			name: `When ${fieldLabel(rule.matchField)} ${rule.matchOperator.toLowerCase().replace(/_/g, ' ')} "${rule.matchValue}"`,
			does: `Classifies matching rows as ${classLabel(rule.classification)}.${
				rule.fkClientId ? '' : ' Every client.'
			}${rule.reason ? ` ${rule.reason}` : ''}`,
		})),
		...rows.map((analysis) => ({
			id: analysis.slug,
			mine: false,
			code: analysis.code,
			name: analysis.name,
			does: analysis.blurb,
		})),
	];

	const term = filters.search.trim().toLowerCase();
	const visible = term
		? flat.filter((row) =>
				`${row.code} ${row.name} ${row.does}`
					.toLowerCase()
					.includes(term),
			)
		: flat;

	const columns: Array<Column<CheckRow>> = [
		{
			key: 'code',
			label: 'Ref.',
			width: 110,
			sortValue: (row) => row.code,
			render: (row) => (
				<>
					{row.code}
					{row.mine && (
						<>
							{' '}
							<span className="bdg gray plain">
								Classification rule
							</span>
						</>
					)}
				</>
			),
		},
		{
			key: 'name',
			label: 'Analysis or rule',
			width: 330,
			sortValue: (row) => row.name,
			render: (row) => row.name,
		},
		{
			key: 'does',
			label: 'What it checks',
			width: 460,
			sortValue: (row) => row.does,
			render: (row) => <span className="small mut">{row.does}</span>,
		},
	];

	return (
		<>
			<FilterBar
				specs={[
					{
						key: 'search',
						label: 'Search',
						kind: 'search',
						placeholder: 'Ref. or name',
					},
				]}
				values={filters}
				onChange={(_key, value) => setFilters({ search: value })}
				onClear={() => setFilters({ search: '' })}
				resultNote={`${count(visible.length)} of ${count(flat.length)}`}
				actions={fullScreen.button}
			/>
			<ErrorNotice error={ownError} onRetry={onRetryOwn} />
			{/* Which rules these are, said rather than left to be guessed
          (Finding 3). The table is the live set for everybody; a draft, if one
          exists, is named as waiting rather than shown as though it ran. */}
			{draftCount > 0 && (
				<Note
					tone="warn"
					title={`${draftCount} unpublished rule${draftCount === 1 ? '' : 's'} waiting in a draft`}
					row
				>
					The rules below are the published set. Publish the draft
					from Classification rules to use it.
				</Note>
			)}
			<Card>
				<DataTable
					rows={visible}
					columns={columns}
					rowKey={(row) => row.id}
					caption="Analyses and classification rules"
					fullScreen={fullScreen}
					maxHeight="main"
					empty={
						<EmptyState title="Nothing matches that">
							Clear the search to see everything.
						</EmptyState>
					}
				/>
			</Card>
		</>
	);
}

/* ═══ analysis settings (admin only) ═══════════════════════════════════════
   One row per client analysis (#1–#34), like the Analyses tab: what it reads,
   the fields it needs, and the one configurable bit per firm — whether its
   exception findings are saved. The setting is stored per internal report,
   so the switch sets every report of the analysis at once. Mutate then
   invalidate; no optimistic update. */

interface SettingRow {
	analysis: ClientAnalysis;
	reports: FirmAnalysisSetting[];
}

/** The reports with an exception rule: the ones a switch can change. */
const switchable = (row: SettingRow) =>
	row.reports.filter(
		(report) => report.exceptionCalculation !== 'NOT_IMPLEMENTED',
	);

function AnalysisSettingsTab({ module }: { module: ModuleCode }) {
	const toast = useToast();
	const settings = useFirmAnalysisSettingsQuery(true, module);
	const update = useUpdateAnalysisSettingMutation();
	const fullScreen = useFullScreen('Analysis settings');
	const [saving, setSaving] = useState(false);
	const [filters, setFilters] = useState({ search: '' });

	const bySlug = new Map(
		(settings.data?.analyses ?? []).map((report) => [report.slug, report]),
	);
	const rows: SettingRow[] = (settings.data?.clientAnalyses ?? []).map(
		(analysis) => ({
			analysis,
			reports: analysis.reports.flatMap(
				(report) => bySlug.get(report.slug) ?? [],
			),
		}),
	);
	const term = filters.search.trim().toLowerCase();
	const visible = term
		? rows.filter((row) =>
				`#${row.analysis.number} ${row.analysis.title} ${row.analysis.description}`
					.toLowerCase()
					.includes(term),
			)
		: rows;

	/* Sets every report of the analysis, one after another. The list is
     refetched after each, so the screen always shows what was saved. */
	const toggle = async (row: SettingRow, on: boolean) => {
		setSaving(true);
		try {
			for (const report of switchable(row))
				await update.mutateAsync({
					slug: report.slug,
					exceptionCalculationEnabled: on,
				});
		} catch (error) {
			toast(
				'danger',
				error instanceof Error
					? error.message
					: 'Could not change the setting.',
			);
		} finally {
			setSaving(false);
		}
	};

	const columns: Array<Column<SettingRow>> = [
		{
			key: 'ref',
			label: 'Ref.',
			width: 64,
			sortValue: (row) => row.analysis.number,
			render: (row) => `#${row.analysis.number}`,
		},
		{
			/* No fixed width: the description takes the room the others leave. */
			key: 'name',
			label: 'Analysis',
			sortValue: (row) => row.analysis.title,
			render: (row) => (
				<Typography
					component="span"
					sx={{ fontSize: 'inherit', lineHeight: 'var(--lh-snug)' }}
				>
					<Box
						component="span"
						sx={{ fontWeight: 600, color: 'var(--ink)' }}
					>
						{row.analysis.title}
						{row.analysis.description ? ':' : ''}
					</Box>{' '}
					<Box
						component="span"
						sx={{
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
						}}
					>
						{row.analysis.description}
					</Box>
				</Typography>
			),
		},
		{
			key: 'required',
			label: 'Required fields',
			width: 220,
			render: (row) => {
				const labels = [
					...new Set(
						row.reports.flatMap((report) =>
							report.requiredColumns.map(
								(column) => column.label,
							),
						),
					),
				];
				return labels.length > 0 ? (
					labels.join(', ')
				) : (
					<span className="mut">—</span>
				);
			},
		},
		{
			key: 'data',
			label: 'Analysis Based on',
			width: 140,
			render: (row) => {
				const types = [
					...new Set(row.reports.map((report) => report.dataType)),
				];
				if (types.length === 0) return <span className="mut">—</span>;
				return dataTypeLabel(
					types.length === 1 ? types[0]! : 'BOTH',
					module,
				);
			},
		},
		{
			key: 'exceptions',
			label: 'Exception calculation',
			width: 170,
			render: (row) => {
				const reports = switchable(row);
				if (row.analysis.onHold || reports.length === 0)
					return (
						<Tooltip
							title={
								row.analysis.onHold
									? `On hold: ${row.analysis.onHold}`
									: 'Exception rules have not been set up for this analysis yet.'
							}
						>
							<span className="mut small">Not available</span>
						</Tooltip>
					);
				const on = reports.every(
					(report) => report.exceptionCalculation === 'ENABLED',
				);
				return (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
						}}
					>
						<Switch
							size="small"
							checked={on}
							disabled={saving}
							inputProps={{
								'aria-label': `Exception calculation for #${row.analysis.number} ${row.analysis.title}`,
							}}
							onChange={(event) =>
								void toggle(row, event.target.checked)
							}
						/>
						{/* Hidden while the Exceptions tab is.
            {row.reports.some((report) => report.raisesExceptions) && (
              <Typography
                component="span"
                sx={{
                  fontSize: 'var(--fs-small)',
                  color: 'var(--ink-3)',
                  lineHeight: 'var(--lh-snug)',
                }}
              >
                Also raised on the Exceptions tab
              </Typography>
            )}
            */}
					</Box>
				);
			},
		},
	];

	return (
		<>
			<ErrorState error={settings.error} onRetry={settings.refetch} />
			<FilterBar
				specs={[
					{
						key: 'search',
						label: 'Search',
						kind: 'search',
						placeholder: 'Ref. or name',
					},
				]}
				values={filters}
				onChange={(_key, value) => setFilters({ search: value })}
				onClear={() => setFilters({ search: '' })}
				resultNote={`${count(visible.length)} of ${count(rows.length)}`}
				actions={fullScreen.button}
			/>
			{/* The card alone, no padding, so the table runs edge to edge. */}
			<Box className="card">
				<div className="card-bd flush">
					<DataTable
						rows={visible}
						columns={columns}
						rowKey={(row) => String(row.analysis.number)}
						caption="Analysis settings"
						fullScreen={fullScreen}
						maxHeight="main"
						loading={settings.isPending}
						initialSort={{ key: 'ref', direction: 'asc' }}
						empty={
							term ? (
								<EmptyState title="Nothing matches that">
									Clear the search to see every analysis.
								</EmptyState>
							) : (
								<EmptyState title="No analyses in the catalogue">
									There is nothing to configure yet.
								</EmptyState>
							)
						}
					/>
				</div>
			</Box>
		</>
	);
}
